/**
 * The product page a shopper decides on.
 *
 * The card in the shop grid is a teaser; this is where someone reads the pack size, looks
 * at the ingredients panel and commits. So it leads with the gallery and keeps price,
 * size and the buy control together in one column beside it, rather than making anyone
 * scroll between the photo and the button.
 *
 * The gallery puts the platform's curated photos FIRST and the vendor's own after them.
 * That is the same rule the catalogue exists for: one product should look like one
 * product in every shop selling it, while a vendor can still show their actual stock.
 */
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Bell, BellRing, Loader2, Minus, Plus, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { FavouriteProductButton } from '@/components/favourite-product-button';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import { ProductGallery, type GalleryImage } from '@/components/product-gallery';
import { use_shop_product } from '@/features/shop/hooks';
import type { ProductVariant } from '@/features/product-variant/types';
import { use_catalog_product } from '@/features/catalog-product/hooks';
import { use_watch_variant } from '@/features/stock/hooks';
import { use_cart } from '@/features/commerce/cart';

export function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const vertical = params.get('vertical') === 'pharmacy' ? 'pharmacy' : 'food';

  const product_q = use_shop_product(vertical, id);
  const product = product_q.data;

  /*
   * The master's gallery, fetched only when this listing is linked. A second request on a
   * detail page is cheap; widening the shop LIST payload with every master's full gallery
   * to save it would not be.
   */
  const master_q = use_catalog_product(product?.catalog_product_id ?? undefined);

  const cart = use_cart();
  const watch_m = use_watch_variant();
  const [variant_id, set_variant_id] = useState<string | null>(null);
  const [watched, set_watched] = useState<Record<string, boolean>>({});
  const [error, set_error] = useState<string | null>(null);

  const selected = useMemo(() => {
    if (!product) return null;
    return product.variants.find((v: ProductVariant) => v.id === variant_id) ?? product.variants[0] ?? null;
  }, [product, variant_id]);

  const images: GalleryImage[] = useMemo(() => {
    if (!product) return [];
    const curated = (master_q.data?.images ?? []).map((i: { path: string }) => ({
      full_path: `${i.path}?variant=full`,
      thumb_path: `${i.path}?variant=thumb`,
      is_curated: true,
    }));
    const own = (product.images ?? []).map((i) => ({
      full_path: i.full_path,
      thumb_path: i.thumb_path,
      is_curated: false,
    }));
    /*
     * Curated first, vendor's after. If the master has no gallery yet, the vendor's own
     * photos are all there is — better than an empty frame on a real listing.
     */
    return [...curated, ...own];
  }, [product, master_q.data]);

  if (product_q.isLoading) {
    return (
      <div className="flex items-center justify-center p-16 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (product_q.error || !product) {
    return (
      <div className="p-4">
        <ErrorState
          data-testid="pdp-error"
          title="We couldn't find that product"
          description="It may have sold out or been taken down. Try the shop."
          action={
            <Link to="/app/shop" className="text-sm font-semibold text-primary">
              Back to shop
            </Link>
          }
        />
      </div>
    );
  }
  if (!selected) {
    return <EmptyState title="This product has no sizes listed" data-testid="pdp-no-variants" />;
  }

  const qty = cart.qty_of(selected.id);
  /*
   * Buyable, not merely in stock. `is_in_stock` answers the count alone, so a size on the
   * shelf under a listing the vendor switched off reports true while checkout refuses it.
   * Falls back to the old flag when the server has not sent the newer one.
   */
  const can_buy = selected.is_purchasable ?? selected.is_in_stock !== false;
  const blocked_reason = selected.unavailable_reason ?? (can_buy ? null : 'OUT_OF_STOCK');
  /** Review and suspension are not waiting on a restock, so the API refuses those watches. */
  const watchable = blocked_reason === 'OUT_OF_STOCK' || blocked_reason === 'LISTING_PAUSED';

  async function notify_me() {
    set_error(null);
    try {
      const res = await watch_m.mutateAsync({
        vertical: vertical === 'food' ? 'FOOD' : 'PHARMACY',
        variant_id: selected!.id,
      });
      // `already_available` is the honest field; the alias is read only for older APIs.
      const available = res.already_available ?? res.already_in_stock;
      set_watched((w) => ({ ...w, [selected!.id]: !available }));
    } catch (e) {
      set_error(form_error_message(e, 'Could not set the alert.'));
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4" data-testid={`pdp-${product.id}`}>
      <Link
        to={`/app/shop?vertical=${vertical}`}
        className="flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary"
        data-testid="pdp-back"
      >
        <ArrowLeft className="h-4 w-4" /> Shop
      </Link>

      <div className="grid gap-6 md:grid-cols-2">
        <ProductGallery images={images} alt={product.name} />

        <div className="flex flex-col gap-3">
          <div>
            <div className="flex items-start gap-2">
              <h1 className="flex-1 font-heading text-xl font-bold leading-snug" data-testid="pdp-name">
                {product.name}
              </h1>
              {product.is_prescription_required && (
                <StatusPill tone="warning" data-testid="pdp-rx">
                  Rx
                </StatusPill>
              )}
              {/* Beside the name, where a shopper who has decided expects to find it. */}
              <FavouriteProductButton
                vertical={vertical}
                product_id={product.id}
                product_name={product.name}
              />
            </div>
            {product.brand && <p className="text-sm text-muted-foreground">{product.brand}</p>}
          </div>

          {/* One price, already inclusive of anything we take. */}
          <div className="flex items-baseline gap-2 tabular-nums">
            <span className="font-heading text-2xl font-bold" data-testid="pdp-price">
              ₹{selected.price_inr}
            </span>
            {selected.mrp_inr && (
              <span className="text-sm text-muted-foreground line-through">₹{selected.mrp_inr}</span>
            )}
            {selected.discount_pct != null && (
              <span className="text-sm font-semibold text-success" data-testid="pdp-discount">
                {selected.discount_pct}% off
              </span>
            )}
          </div>

          {product.variants.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Size</span>
              <div className="flex flex-wrap gap-2" data-testid="pdp-variants">
                {product.variants.map((v: ProductVariant) => {
                  const gone = !(v.is_purchasable ?? v.is_in_stock !== false);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => set_variant_id(v.id)}
                      title={
                        !gone
                          ? undefined
                          : v.unavailable_reason === 'LISTING_PAUSED'
                            ? `${v.label} is not on sale right now`
                            : `${v.label} is out of stock`
                      }
                      className={cn(
                        'rounded-button border-2 px-3 py-1.5 text-sm font-medium transition-colors',
                        v.id === selected.id
                          ? 'border-primary text-primary'
                          : 'border-brand-outline-variant/25 text-muted-foreground hover:border-primary/40',
                        // Out-of-stock sizes stay pickable: a shopper has to be able to
                        // select the one they want in order to ask about it.
                        gone && 'line-through opacity-60',
                      )}
                      data-testid={`pdp-variant-${v.id}`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.pack_size && <p className="text-sm text-muted-foreground">Pack size: {product.pack_size}</p>}

          {/* Buy, or ask to be told. */}
          <div className="mt-1">
            {!can_buy ? (
              <div className="flex flex-col gap-1.5" data-testid="pdp-out-of-stock">
                <p className="text-sm font-semibold text-destructive">
                  {blocked_reason === 'LISTING_PAUSED'
                    ? 'Not on sale right now'
                    : blocked_reason === 'NOT_APPROVED'
                      ? 'Awaiting review'
                      : blocked_reason === 'VENDOR_UNAVAILABLE'
                        ? 'Seller unavailable'
                        : 'Out of stock'}
                </p>
                <button
                  type="button"
                  // Nothing to promise when review or vendor standing is the blocker.
                  disabled={watch_m.isPending || !watchable}
                  onClick={notify_me}
                  className="w-fit rounded-button border-2 border-brand-outline-variant/30 px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60"
                  data-testid="pdp-notify"
                >
                  {!watchable ? (
                    <>Not available to order</>
                  ) : watched[selected.id] ? (
                    <>
                      <BellRing className="mr-1 inline h-4 w-4" /> We&apos;ll tell you
                    </>
                  ) : (
                    <>
                      <Bell className="mr-1 inline h-4 w-4" /> Notify me when it&apos;s back
                    </>
                  )}
                </button>
              </div>
            ) : qty === 0 ? (
              <button
                type="button"
                onClick={() => cart.add(selected.id)}
                className="w-full rounded-button bg-primary px-4 py-2.5 font-semibold text-white active:scale-95 sm:w-auto"
                data-testid="pdp-add"
              >
                <Plus className="mr-1 inline h-4 w-4" /> Add to cart
              </button>
            ) : (
              <div className="flex items-center gap-3" data-testid="pdp-stepper">
                <button
                  type="button"
                  onClick={() => cart.set_qty(selected.id, qty - 1)}
                  aria-label="Decrease"
                  className="rounded-full bg-secondary p-2 active:scale-90"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-6 text-center font-semibold tabular-nums">{qty}</span>
                <button
                  type="button"
                  onClick={() => cart.inc(selected.id)}
                  aria-label="Increase"
                  className="rounded-full bg-primary/10 p-2 text-primary active:scale-90"
                >
                  <Plus className="h-4 w-4" />
                </button>
                {/* Client change (9 Oct 2026): once in the cart, offer the way to checkout. */}
                <Link
                  to={`/app/shop?vertical=${vertical}&cart=open`}
                  className="ml-2 rounded-button bg-primary px-4 py-2.5 font-semibold text-white active:scale-95"
                  data-testid="pdp-go-to-checkout"
                >
                  Go to checkout
                </Link>
              </div>
            )}
            {error && (
              <p className="mt-1.5 text-sm text-destructive" role="alert" data-testid="pdp-error-message">
                {error}
              </p>
            )}
          </div>

          {product.is_prescription_required && (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              You&apos;ll be asked to upload a prescription at checkout.
            </p>
          )}
        </div>
      </div>

      {product.description && (
        <Card>
          <CardContent className="p-4">
            <h2 className="mb-1 font-heading text-base font-bold">About this product</h2>
            <p className="whitespace-pre-line text-sm text-muted-foreground" data-testid="pdp-description">
              {product.description}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
